import { useState, useRef, useCallback } from 'react';

interface UseTextToSpeechOptions {
  onStart?: () => void;
  onEnd?: () => void;
  onError?: (error: Error) => void;
}

// Browser-native TTS fallback
const speakWithBrowser = (
  text: string, 
  onStart?: () => void, 
  onEnd?: () => void
): SpeechSynthesisUtterance | null => {
  if (!window.speechSynthesis) return null;

  // Cancel any ongoing speech
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = 'en-US';
  utterance.rate = 0.95;
  utterance.pitch = 1;
  utterance.volume = 1;

  utterance.onstart = () => onStart?.();
  utterance.onend = () => onEnd?.();
  utterance.onerror = () => onEnd?.();

  window.speechSynthesis.speak(utterance);
  return utterance;
};

// Stop browser TTS
const stopBrowserSpeech = () => {
  if (window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
};

// Clean text for speech (remove markdown, links, emojis, etc.)
const cleanTextForSpeech = (text: string): string => {
  return text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove markdown links, keep text
    .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold
    .replace(/\*([^*]+)\*/g, '$1') // Remove italic
    .replace(/#+\s/g, '') // Remove headers
    .replace(/https?:\/\/[^\s]+/g, '') // Remove URLs
    .replace(/[🚗💰📍✨🎉🎊⭐️💎✓]/g, '') // Remove common emojis
    .replace(/\|/g, ',') // Replace table pipes with commas
    .replace(/-{2,}/g, '') // Remove horizontal rules
    .replace(/\n{2,}/g, '. ') // Convert double newlines to sentences
    .replace(/\n/g, ' ') // Convert single newlines to spaces
    .replace(/\s{2,}/g, ' ') // Collapse multiple spaces
    .trim();
};

export const useTextToSpeech = (options: UseTextToSpeechOptions = {}) => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentUrlRef = useRef<string | null>(null);
  const browserUtteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  const speak = useCallback(async (text: string) => {
    if (!text || typeof text !== 'string' || !text.trim()) return;

    // Stop any current playback (both ElevenLabs and browser)
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current);
      currentUrlRef.current = null;
    }
    stopBrowserSpeech();

    const cleanText = cleanTextForSpeech(text);
    if (!cleanText) return;

    setIsLoading(true);
    setUsingFallback(false);

    try {
      // Try ElevenLabs first
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ text: cleanText }),
        }
      );

      if (!response.ok) {
        throw new Error('ElevenLabs TTS failed');
      }

      const audioBlob = await response.blob();
      
      // Verify we got actual audio data
      if (audioBlob.size < 1000) {
        throw new Error('Invalid audio response');
      }

      const audioUrl = URL.createObjectURL(audioBlob);
      currentUrlRef.current = audioUrl;

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => {
        setIsSpeaking(true);
        setIsLoading(false);
        options.onStart?.();
      };

      audio.onended = () => {
        setIsSpeaking(false);
        if (currentUrlRef.current) {
          URL.revokeObjectURL(currentUrlRef.current);
          currentUrlRef.current = null;
        }
        audioRef.current = null;
        options.onEnd?.();
      };

      audio.onerror = () => {
        // Audio failed to play - fall back to browser TTS
        console.warn('ElevenLabs audio playback failed, using browser TTS');
        setIsLoading(false);
        useBrowserFallback(cleanText);
      };

      await audio.play();
    } catch (error) {
      // ElevenLabs failed - fall back to browser TTS silently
      console.warn('ElevenLabs TTS failed, falling back to browser TTS:', error);
      useBrowserFallback(cleanText);
    }
  }, [options]);

  const useBrowserFallback = useCallback((text: string) => {
    setUsingFallback(true);
    setIsLoading(false);
    setIsSpeaking(true);
    options.onStart?.();

    browserUtteranceRef.current = speakWithBrowser(
      text,
      undefined,
      () => {
        setIsSpeaking(false);
        browserUtteranceRef.current = null;
        options.onEnd?.();
      }
    );

    // If browser TTS isn't available at all, just end gracefully
    if (!browserUtteranceRef.current) {
      setIsSpeaking(false);
      options.onEnd?.();
    }
  }, [options]);

  const stop = useCallback(() => {
    // Stop ElevenLabs audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current);
      currentUrlRef.current = null;
    }
    
    // Stop browser TTS
    stopBrowserSpeech();
    browserUtteranceRef.current = null;
    
    setIsSpeaking(false);
    setIsLoading(false);
  }, []);

  return {
    speak,
    stop,
    isSpeaking,
    isLoading,
    usingFallback,
  };
};
